import React, { FC, useContext, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "@natera/form";
import { usePhoneValidation } from "@natera/platform/lib/hooks";
import { ConfigContext } from "@app/provider";
import {
  formatPhoneNumberInput,
  isPatientMinor,
  stripPhoneFormatting,
  validateEmail,
  validateZipCode,
} from "@app/utils";
import { messages } from "./messages";
import { PublicGisOrderLookupContext } from "./provider";
import { writeGuestLookupPrefill } from "@app/pages/private/scheduleAGeneticInformationSession/context";
import { PublicGisLookupData, PublicGisLookupIdentifier } from "./types";
import PortalPhoneImage from "@assets/images/gis_entry_portal_phone.png";

/** Payload passed to `onSubmit` so the parent can match without waiting for context setState. */
export interface LookupStepperSubmitPayload {
  lookupData: PublicGisLookupData;
  lookupIdentifier: PublicGisLookupIdentifier;
  recaptchaValue: string;
}

const MONTHS = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const DAYS = Array.from({ length: 31 }, (_, index) => String(index + 1));

type ValidationField =
  | "firstName"
  | "lastName"
  | "dateOfBirth"
  | "recaptcha"
  | PublicGisLookupIdentifier;

/** Outcome reported by the reCAPTCHA widget that invalidated an earlier check. */
type RecaptchaNotice = "expired" | "errored" | null;

interface FieldRowProps {
  id: string;
  label: string;
  type?: string;
  maxLength?: number;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  placeholder?: string;
  ariaLabel?: string;
  required?: boolean;
}

const FieldRow: FC<FieldRowProps> = ({
  id,
  label,
  type = "text",
  maxLength,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  ariaLabel,
  required = false,
}) => (
  <div className="public-gis-lookup__field">
    {label && (
      <label className="public-gis-lookup__label" htmlFor={id}>
        {label}
        {required && (
          <span className="public-gis-lookup__required" aria-hidden>
            *
          </span>
        )}
      </label>
    )}
    <input
      id={id}
      className={`public-gis-lookup__input${
        error ? " public-gis-lookup__input--error" : ""
      }`}
      type={type}
      maxLength={maxLength}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      aria-label={ariaLabel}
      aria-invalid={error ? true : undefined}
      aria-required={required || undefined}
      aria-describedby={error ? `${id}-error` : undefined}
    />
    {error && (
      <span
        id={`${id}-error`}
        className="public-gis-lookup__error"
        role="alert"
      >
        {error}
      </span>
    )}
  </div>
);

interface LookupStepperProps {
  onSubmit: (payload: LookupStepperSubmitPayload) => void;
}

/** Oldest birth year accepted, matching the prenatal education DOB check. */
const MAX_PATIENT_AGE_YEARS = 120;

function buildDateOfBirth(month: string, day: string, year: string): string {
  if (!month || !day || !/^\d{4}$/.test(year)) return "";

  const paddedDay = day.padStart(2, "0");
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() + 1 !== Number(month) ||
    date.getUTCDate() !== Number(day)
  ) {
    return "";
  }

  const today = new Date();
  const todayUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  if (date.getTime() > todayUtc) return "";

  // Older than any living patient, so it's a typo (1092 for 1992) rather than
  // a birth date worth running a lookup on.
  const oldestUtc = Date.UTC(
    today.getFullYear() - MAX_PATIENT_AGE_YEARS,
    today.getMonth(),
    today.getDate()
  );
  if (date.getTime() < oldestUtc) return "";

  return `${year}-${month}-${paddedDay}`;
}

const MAX_NAME_LENGTH = 255;

/** Splits a stored `YYYY-MM-DD` date into month/day/year form values. */
const splitDateOfBirth = (
  dob?: string
): { month: string; day: string; year: string } => {
  if (!dob) return { month: "", day: "", year: "" };
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  if (!match) return { month: "", day: "", year: "" };
  const [, year, month, day] = match;
  return { month, day: String(Number(day)), year };
};

const resolveSelectedIdentifier = (
  data?: PublicGisLookupData
): PublicGisLookupIdentifier => {
  if (data?.email?.trim()) return "email";
  if (data?.zipCode?.trim()) return "zipCode";
  if (data?.mobileNumber?.trim()) return "mobileNumber";
  return "zipCode";
};

/**
 * GIS-specific public guest lookup form. Visual treatment follows the Figma
 * public guest lookup, while preserving the existing order lookup submission.
 */
const LookupStepper: FC<LookupStepperProps> = ({ onSubmit }) => {
  const intl = useIntl();
  const { config } = useContext(ConfigContext);
  const { validatePhone } = usePhoneValidation();
  const {
    lookupData,
    saveLookupData,
    lookupIdentifier,
    setLookupIdentifier,
    setRecaptchaValue,
  } = useContext(PublicGisOrderLookupContext);

  const siteKey = config.recaptcha?.v2SiteKey;
  const usePlusSignInEmailAddress = Boolean(
    config.test?.usePlusSignInEmailAddress?.enabled
  );
  const initialDob = splitDateOfBirth(lookupData?.dateOfBirth);
  const [verified, setVerified] = useState<boolean>(!siteKey);
  const [recaptchaNotice, setRecaptchaNotice] = useState<RecaptchaNotice>(null);
  // Bumped to remount the widget; see `handleRecaptchaExpired`.
  const [recaptchaInstance, setRecaptchaInstance] = useState(0);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [firstName, setFirstName] = useState(lookupData?.firstName ?? "");
  const [lastName, setLastName] = useState(lookupData?.lastName ?? "");
  const [birthMonth, setBirthMonth] = useState(initialDob.month);
  const [birthDay, setBirthDay] = useState(initialDob.day);
  const [birthYear, setBirthYear] = useState(initialDob.year);
  const [selectedIdentifier, setSelectedIdentifier] =
    useState<PublicGisLookupIdentifier>(
      () => lookupIdentifier ?? resolveSelectedIdentifier(lookupData)
    );
  const [zipCode, setZipCode] = useState(lookupData?.zipCode ?? "");
  const [mobileNumber, setMobileNumber] = useState(
    formatPhoneNumberInput(lookupData?.mobileNumber ?? "")
  );
  const [email, setEmail] = useState(lookupData?.email ?? "");
  const [touchedFields, setTouchedFields] = useState<
    Partial<Record<ValidationField, boolean>>
  >({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const dateOfBirth = useMemo(
    () => buildDateOfBirth(birthMonth, birthDay, birthYear),
    [birthMonth, birthDay, birthYear]
  );

  const markFieldTouched = (field: ValidationField) => {
    setTouchedFields((prevState) =>
      prevState[field] ? prevState : { ...prevState, [field]: true }
    );
  };

  const getNameError = (value: string): string => {
    if (!value.trim()) return intl.formatMessage(messages.fieldRequired);
    if (value.length > MAX_NAME_LENGTH) {
      return intl.formatMessage(messages.nameTooLong, {
        maxLength: MAX_NAME_LENGTH,
      });
    }
    return "";
  };

  const getDateOfBirthError = (): string => {
    if (!birthMonth || !birthDay || birthYear.trim().length < 4) {
      return intl.formatMessage(messages.fieldRequired);
    }
    if (!dateOfBirth) {
      return intl.formatMessage(messages.invalidDateOfBirth);
    }
    const [year, month, day] = dateOfBirth.split("-").map(Number);
    if (isPatientMinor(new Date(year, month - 1, day))) {
      return intl.formatMessage(messages.under18DateOfBirth);
    }
    return "";
  };

  const getIdentifierError = (
    identifier: PublicGisLookupIdentifier
  ): string => {
    const value = {
      zipCode,
      mobileNumber,
      email,
    }[identifier].trim();

    if (!value) return intl.formatMessage(messages.fieldRequired);

    if (identifier === "zipCode" && !validateZipCode(value)) {
      return intl.formatMessage(messages.invalidZipCode);
    }
    if (
      identifier === "mobileNumber" &&
      !validatePhone(stripPhoneFormatting(value))
    ) {
      return intl.formatMessage(messages.invalidMobileNumber);
    }
    if (
      identifier === "email" &&
      !validateEmail(value, usePlusSignInEmailAddress)
    ) {
      return intl.formatMessage(messages.invalidEmail);
    }
    return "";
  };

  const validationErrors: Record<ValidationField, string> = {
    firstName: getNameError(firstName),
    lastName: getNameError(lastName),
    dateOfBirth: getDateOfBirthError(),
    zipCode: getIdentifierError("zipCode"),
    mobileNumber: getIdentifierError("mobileNumber"),
    email: getIdentifierError("email"),
    recaptcha: verified ? "" : intl.formatMessage(messages.recaptchaRequired),
  };

  const getVisibleError = (field: ValidationField): string => {
    if (field === "dateOfBirth") {
      const dateOfBirthIsComplete = Boolean(
        birthMonth && birthDay && birthYear.trim().length === 4
      );
      if (!submitAttempted && !dateOfBirthIsComplete) return "";
    }

    return touchedFields[field] || submitAttempted
      ? validationErrors[field]
      : "";
  };

  const canContinue = Boolean(
    !validationErrors.firstName &&
    !validationErrors.lastName &&
    !validationErrors.dateOfBirth &&
    !validationErrors[selectedIdentifier] &&
    !validationErrors.recaptcha
  );

  const handleRecaptchaChange = (value: string | null) => {
    setVerified(Boolean(value));
    if (value) setRecaptchaNotice(null);
  };

  /**
   * Google draws its own "verification expired" warning inside the widget,
   * layered over the "I'm not a robot" label, which leaves both strings hard to
   * read. Remounting drops that state for a clean, unchecked widget and we
   * explain what happened underneath it in the form's own error styling.
   */
  const handleRecaptchaExpired = () => {
    setVerified(false);
    setRecaptchaValue(null);
    setRecaptchaNotice("expired");
    setRecaptchaInstance((instance) => instance + 1);
  };

  // Errors can repeat (e.g. the widget can't reach Google), so reset in place
  // rather than remounting into a loop of failing widgets.
  const handleRecaptchaErrored = () => {
    setVerified(false);
    setRecaptchaValue(null);
    setRecaptchaNotice("errored");
    recaptchaRef.current?.reset();
  };

  const recaptchaMessage = recaptchaNotice
    ? intl.formatMessage(
        recaptchaNotice === "expired"
          ? messages.recaptchaExpired
          : messages.recaptchaErrored
      )
    : getVisibleError("recaptcha");

  const handleSubmit = () => {
    setSubmitAttempted(true);
    setTouchedFields((prevState) => ({
      ...prevState,
      firstName: true,
      lastName: true,
      dateOfBirth: true,
      [selectedIdentifier]: true,
      recaptcha: true,
    }));

    if (!canContinue) return;

    const savedLookupData: PublicGisLookupData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth,
      mobileNumber: mobileNumber.trim(),
      email: email.trim(),
      zipCode: zipCode.trim(),
    };

    // No site key configured (local/test): use a sentinel so the funnel can
    // proceed without the widget.
    const nextRecaptchaValue = siteKey
      ? (recaptchaRef.current?.getValue() ?? null)
      : "recaptcha-disabled";

    if (!nextRecaptchaValue) return;

    // Persist for downstream screens, but also pass values directly — React
    // setState is async, so `runMatch` would otherwise still see the previous
    // (often null) context values on the first Continue click.
    saveLookupData(savedLookupData);
    setLookupIdentifier(selectedIdentifier);
    writeGuestLookupPrefill({
      firstName: savedLookupData.firstName,
      lastName: savedLookupData.lastName,
      dateOfBirth: savedLookupData.dateOfBirth,
      phone: savedLookupData.mobileNumber,
      email: savedLookupData.email,
      zipCode: savedLookupData.zipCode,
      lookupIdentifier: selectedIdentifier,
    });
    setRecaptchaValue(nextRecaptchaValue);
    onSubmit({
      lookupData: savedLookupData,
      lookupIdentifier: selectedIdentifier,
      recaptchaValue: nextRecaptchaValue,
    });
  };

  const identifierConfig: Record<
    PublicGisLookupIdentifier,
    {
      label: string;
      inputLabel: string;
      placeholder: string;
      type?: string;
      value: string;
      onChange: (value: string) => void;
    }
  > = {
    zipCode: {
      label: intl.formatMessage(messages.zipCode),
      inputLabel: "",
      placeholder: "Enter your zip code",
      value: zipCode,
      onChange: setZipCode,
    },
    mobileNumber: {
      label: intl.formatMessage(messages.mobileNumber),
      inputLabel: "",
      placeholder: "+1-555-123-4567",
      type: "tel",
      value: mobileNumber,
      onChange: (value) => setMobileNumber(formatPhoneNumberInput(value)),
    },
    email: {
      label: intl.formatMessage(messages.email),
      inputLabel: "",
      placeholder: "Enter your email",
      type: "email",
      value: email,
      onChange: setEmail,
    },
  };

  const selectedConfig = identifierConfig[selectedIdentifier];
  const dateOfBirthError = getVisibleError("dateOfBirth");
  const selectedIdentifierError = getVisibleError(selectedIdentifier);

  return (
    <div className="public-gis-lookup">
      <div className="public-gis-lookup__card">
        <div className="public-gis-lookup__hero">
          <img
            className="public-gis-lookup__hero-image"
            src={PortalPhoneImage}
            alt=""
            aria-hidden
          />
          <div className="public-gis-lookup__hero-shade" aria-hidden />
          <div className="public-gis-lookup__hero-message">
            <h2 className="public-gis-lookup__portal-heading">
              {intl.formatMessage(messages.portalHeading)}
            </h2>
            <p className="public-gis-lookup__portal-body">
              {intl.formatMessage(messages.stepperSubtitle)}
            </p>
          </div>
        </div>

        <div className="public-gis-lookup__content">
          <h1 className="public-gis-lookup__title">
            {intl.formatMessage(messages.stepperTitle)}
          </h1>

          <FieldRow
            id="gis-lookup-first-name"
            label={intl.formatMessage(messages.firstName)}
            maxLength={MAX_NAME_LENGTH}
            value={firstName}
            onChange={setFirstName}
            onBlur={() => markFieldTouched("firstName")}
            error={getVisibleError("firstName")}
            placeholder="Enter your first name"
            required
          />
          <FieldRow
            id="gis-lookup-last-name"
            label={intl.formatMessage(messages.lastName)}
            maxLength={MAX_NAME_LENGTH}
            value={lastName}
            onChange={setLastName}
            onBlur={() => markFieldTouched("lastName")}
            error={getVisibleError("lastName")}
            placeholder="Enter your last name"
            required
          />

          <div className="public-gis-lookup__field">
            <label
              className="public-gis-lookup__label"
              htmlFor="gis-lookup-birth-month"
            >
              {intl.formatMessage(messages.dateOfBirth)}
              <span className="public-gis-lookup__required" aria-hidden>
                *
              </span>
            </label>
            <div className="public-gis-lookup__dob-row">
              <select
                id="gis-lookup-birth-month"
                className={`public-gis-lookup__input public-gis-lookup__input--select${
                  !birthMonth ? " public-gis-lookup__input--placeholder" : ""
                }${dateOfBirthError ? " public-gis-lookup__input--error" : ""}`}
                value={birthMonth}
                onChange={(e) => setBirthMonth(e.target.value)}
                onBlur={() => markFieldTouched("dateOfBirth")}
                aria-invalid={dateOfBirthError ? true : undefined}
                aria-describedby={
                  dateOfBirthError
                    ? "gis-lookup-date-of-birth-error"
                    : undefined
                }
              >
                <option value="">Month</option>
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              <select
                aria-label="Birth day"
                className={`public-gis-lookup__input public-gis-lookup__input--select${
                  !birthDay ? " public-gis-lookup__input--placeholder" : ""
                }${dateOfBirthError ? " public-gis-lookup__input--error" : ""}`}
                value={birthDay}
                onChange={(e) => setBirthDay(e.target.value)}
                onBlur={() => markFieldTouched("dateOfBirth")}
                aria-invalid={dateOfBirthError ? true : undefined}
                aria-describedby={
                  dateOfBirthError
                    ? "gis-lookup-date-of-birth-error"
                    : undefined
                }
              >
                <option value="">Day</option>
                {DAYS.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <input
              aria-label="Birth year"
              className={`public-gis-lookup__input${
                dateOfBirthError ? " public-gis-lookup__input--error" : ""
              }`}
              inputMode="numeric"
              maxLength={4}
              placeholder="Year"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, ""))}
              onBlur={() => markFieldTouched("dateOfBirth")}
              aria-invalid={dateOfBirthError ? true : undefined}
              aria-describedby={
                dateOfBirthError ? "gis-lookup-date-of-birth-error" : undefined
              }
            />
            {dateOfBirthError && (
              <span
                id="gis-lookup-date-of-birth-error"
                className="public-gis-lookup__error"
                role="alert"
              >
                {dateOfBirthError}
              </span>
            )}
          </div>

          <p className="public-gis-lookup__identifier-label">
            {intl.formatMessage(messages.oneOfFollowing)}
            <span className="public-gis-lookup__required" aria-hidden>
              *
            </span>
          </p>
          <div className="public-gis-lookup__chips" role="group">
            {(Object.keys(identifierConfig) as PublicGisLookupIdentifier[]).map(
              (key) => {
                const selected = selectedIdentifier === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`public-gis-lookup__chip${
                      selected ? " public-gis-lookup__chip--selected" : ""
                    }`}
                    aria-pressed={selected}
                    onClick={() => setSelectedIdentifier(key)}
                  >
                    {selected && <span aria-hidden>✓</span>}
                    {identifierConfig[key].label}
                  </button>
                );
              }
            )}
          </div>

          <FieldRow
            id={`gis-lookup-${selectedIdentifier}`}
            label={selectedConfig.inputLabel}
            type={selectedConfig.type}
            value={selectedConfig.value}
            onChange={selectedConfig.onChange}
            onBlur={() => markFieldTouched(selectedIdentifier)}
            error={selectedIdentifierError}
            placeholder={selectedConfig.placeholder}
            ariaLabel={selectedConfig.label}
          />

          {siteKey && (
            <div className="public-gis-lookup__recaptcha">
              <ReCAPTCHA
                key={recaptchaInstance}
                className="recaptcha"
                sitekey={siteKey}
                ref={recaptchaRef}
                onChange={handleRecaptchaChange}
                onExpired={handleRecaptchaExpired}
                onErrored={handleRecaptchaErrored}
              />
              {recaptchaMessage && (
                <span className="public-gis-lookup__error" role="alert">
                  {recaptchaMessage}
                </span>
              )}
            </div>
          )}

          <Button
            className="public-gis-lookup__btn-primary"
            unelevated
            disabled={!canContinue}
            onClick={handleSubmit}
          >
            {intl.formatMessage(messages.continue)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LookupStepper;
